/**
 * HTTP interceptor that manages error handling, retry logic, and loading spinners
 * for outgoing HTTP requests.
 */

import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { AlertController, ToastController } from '@ionic/angular/standalone';
import { from, Observable, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  retry,
  switchMap,
  tap,
  timeout,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ProinfraBasisLoadingSpinnerService } from '@profi/proinfra-shared-angular';
import { AuthenticationService } from '@app/auth/services/authentication.service';

/**
 * Mapping of HTTP status codes to error route paths.
 */
interface ErrorRoutes {
  [HttpStatusCode.BadRequest]: string;
  [HttpStatusCode.Unauthorized]: string;
  [HttpStatusCode.Forbidden]: string;
  [HttpStatusCode.NotFound]: string;
  [HttpStatusCode.InternalServerError]: string;
  [HttpStatusCode.ServiceUnavailable]: string;
}

@Injectable({ providedIn: 'root' })
export class HttpRequestInterceptor implements HttpInterceptor {
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 2000;

  constructor(
    private router: Router,
    private toastController: ToastController,
    private alertController: AlertController,
    private translateService: TranslateService,
    private loadingSpinnerService: ProinfraBasisLoadingSpinnerService,
    private authenticationService: AuthenticationService
  ) {}

  /**
   * Intercepts outgoing HTTP requests and applies loading spinner,
   * error handling, and retry logic.
   *
   * @param request - The outgoing HTTP request
   * @param next - The next interceptor in the chain
   * @returns An observable of the HTTP event
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const shouldShowSpinner =
      !this.isAuthRequest(request) &&
      !this.isChatbotRequest(request) &&
      !this.shouldIgnoreLoading(request);

    if (!shouldShowSpinner) {
      return next.handle(request).pipe(
        catchError((error: HttpErrorResponse) => {
          if (
            request.url.includes('/auth') &&
            error.status === HttpStatusCode.Forbidden
          ) {
            return throwError(() => error);
          }
          return this.handleError(error);
        })
      );
    }

    return from(this.loadingSpinnerService.show()).pipe(
      switchMap(() =>
        next.handle(request).pipe(
          timeout(15000),
          catchError((error: HttpErrorResponse) => this.handleError(error)),
          finalize(() => this.loadingSpinnerService.hide())
        )
      )
    );
  }

  /**
   * Determines if the loading spinner should be ignored based on request URL.
   *
   * @param request - The HTTP request
   * @returns True if loading should be ignored
   */
  private shouldIgnoreLoading(request: HttpRequest<unknown>): boolean {
    const ignoredPaths = ['/assets/', '/favicon.ico'];
    return ignoredPaths.some(path => request.url.includes(path));
  }

  /**
   * Determines if the request is an authentication request.
   *
   * @param request - The HTTP request
   * @returns True if it is an auth request
   */
  private isAuthRequest(request: HttpRequest<unknown>): boolean {
    return request.url.includes('/auth');
  }

  /**
   * Determines if the request is related to a chatbot.
   *
   * @param request - The HTTP request
   * @returns True if it is a chatbot request
   */
  private isChatbotRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('/chat');
  }

  /**
   * Handles HTTP errors and determines the appropriate response.
   *
   * @param error - The HTTP error response
   * @returns An observable that errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    this.presentFailedAlert(error);
    return this.isRetryableError(error)
      ? this.retryRequest(error)
      : this.handleErrorNavigation(error);
  }

  /**
   * Determines if the error is retryable.
   *
   * @param error - The HTTP error response
   * @returns True if retryable
   */
  private isRetryableError(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.ServiceUnavailable;
  }

  /**
   * Retries a failed HTTP request with delay and shows toast on each retry.
   *
   * @param error - The HTTP error response
   * @returns An observable that eventually errors
   */
  private retryRequest(error: HttpErrorResponse): Observable<never> {
    let retryCount = 0;
    return throwError(() => error).pipe(
      retry({
        count: this.MAX_RETRIES,
        delay: this.RETRY_DELAY,
      }),
      tap(() => {
        retryCount++;
        if (retryCount > 1) {
          this.showRetryToast(retryCount).subscribe();
        }
      }),
      catchError(err => this.handleErrorNavigation(err))
    );
  }

  /**
   * Shows a retry toast message indicating the retry attempt.
   *
   * @param retryCount - The number of retry attempts
   * @returns An observable that resolves when the toast is shown
   */
  private showRetryToast(retryCount: number): Observable<void> {
    return from(
      this.toastController
        .create({
          message: `${this.translateService.instant('labelsInteractionElements.requestFailed')} (${retryCount})`,
          duration: 2000,
        })
        .then(toast => toast.present())
    );
  }

  /**
   * Navigates to the appropriate error page or logs out the user depending on the error.
   *
   * @param error - The HTTP error response
   * @returns An observable that errors after navigation
   */
  private handleErrorNavigation(error: HttpErrorResponse): Observable<never> {
    const errorRoutes: ErrorRoutes = {
      [HttpStatusCode.BadRequest]: `/error/${HttpStatusCode.BadRequest}`,
      [HttpStatusCode.Unauthorized]: `/error/${HttpStatusCode.Unauthorized}`,
      [HttpStatusCode.Forbidden]: `/error/${HttpStatusCode.Forbidden}`,
      [HttpStatusCode.NotFound]: `/error/${HttpStatusCode.NotFound}`,
      [HttpStatusCode.InternalServerError]: `/error/${HttpStatusCode.InternalServerError}`,
      [HttpStatusCode.ServiceUnavailable]: `/error/${HttpStatusCode.ServiceUnavailable}`,
    };

    let route = `/logout`;
    if (
      error.status === HttpStatusCode.Unauthorized ||
      error.status === HttpStatusCode.Forbidden
    ) {
      this.authenticationService.logoutInvalidSession();
    } else {
      route = errorRoutes[error.status];
    }

    return route
      ? from(this.router.navigate([route])).pipe(
          switchMap(() => throwError(() => error))
        )
      : throwError(() => error);
  }

  /**
   * Presents an alert dialog showing the error message.
   *
   * @param error - The HTTP error response
   */
  private presentFailedAlert(error: HttpErrorResponse): void {
    this.alertController
      .create({
        header: 'Error',
        message: error.message,
        buttons: [
          this.translateService.instant('labelsInteractionElements.confirm'),
        ],
      })
      .then(alert => alert.present());
  }
}
